import React, { useState, useEffect } from 'react';
import xService from '../services/xService';
import XSearchForm from '../components/XSearchForm';
import XSearchList from '../components/XSearchList';
import XSearchResultsWithHistory from '../components/XSearchResultsWithHistory';
import XSearchScheduleForm from '../components/XSearchScheduleForm';
import XSearchScheduleList from '../components/XSearchScheduleList';
import XNotificationForm from '../components/XNotificationForm';
import XNotificationList from '../components/XNotificationList';
import XGroupForm from '../components/XGroupForm';
import XGroupList from '../components/XGroupList';

const XSearchPage = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('searches');

  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [apiConfigured, setApiConfigured] = useState(false);

  // Modal states for searches
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedSearch, setSelectedSearch] = useState(null);

  // Results state
  const [showResults, setShowResults] = useState(false);
  const [selectedSearchId, setSelectedSearchId] = useState(null);
  const [runningSearchId, setRunningSearchId] = useState(null);

  // Schedule management states
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleSearch, setScheduleSearch] = useState(null);
  const [scheduleWarnings, setScheduleWarnings] = useState({});
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [isScheduleSubmitting, setIsScheduleSubmitting] = useState(false);
  const [deletingScheduleId, setDeletingScheduleId] = useState(null);

  // Filter and sort states
  const [searchText, setSearchText] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Notification states
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [isEditingNotification, setIsEditingNotification] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isNotificationSubmitting, setIsNotificationSubmitting] = useState(false);

  // Group states
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [isEditingGroup, setIsEditingGroup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    checkApiHealth();
    fetchSearches();
    if (activeTab === 'notifications') {
      fetchNotifications();
    } else if (activeTab === 'groups') {
      fetchGroups();
    }
  }, [activeTab]);

  const checkApiHealth = async () => {
    try {
      const health = await xService.healthCheck();
      setApiConfigured(health.x_api_configured);
      if (!health.x_api_configured) {
        setError('X API is not configured. Please contact administrator to set API credentials.');
      }
    } catch (err) {
      console.error('Error checking API health:', err);
      setError('Failed to connect to X Search service');
    }
  };

  const fetchSearches = async () => {
    try {
      setLoading(true);
      const data = await xService.getAllSearches();
      setSearches(data.searches || []);
      setError('');
    } catch (err) {
      console.error('Error fetching searches:', err);
      setError('Failed to load searches: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsEditing(false);
    setSelectedSearch(null);
    setShowModal(true);
  };

  const handleEdit = (search) => {
    setIsEditing(true);
    setSelectedSearch(search);
    setShowModal(true);
  };

  const handleDelete = async (searchId) => {
    if (!window.confirm('Are you sure you want to delete this search?')) {
      return;
    }

    try {
      await xService.deleteSearch(searchId);
      await fetchSearches();
    } catch (err) {
      console.error('Error deleting search:', err);
      alert('Failed to delete search: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSave = async (formData) => {
    try {
      if (isEditing && selectedSearch) {
        await xService.updateSearch(selectedSearch.id, formData);
      } else {
        await xService.createSearch(formData);
      }
      setShowModal(false);
      await fetchSearches();
    } catch (err) {
      console.error('Error saving search:', err);
      throw err;
    }
  };

  const handleRun = async (searchId) => {
    if (!apiConfigured) {
      alert('X API is not configured. Please contact administrator.');
      return;
    }

    try {
      setRunningSearchId(searchId);
      await xService.runSearch(searchId);
      // After running, show the results with history
      setSelectedSearchId(searchId);
      setShowResults(true);
    } catch (err) {
      console.error('Error running search:', err);
      alert('Failed to run search: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRunningSearchId(null);
    }
  };

  const handleViewResults = async (searchId) => {
    try {
      setSelectedSearchId(searchId);
      setShowResults(true);
    } catch (err) {
      console.error('Error viewing results:', err);
      alert('Failed to load results: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleManageSchedules = (search) => {
    setScheduleSearch(search);
    setScheduleWarnings({});
    setShowScheduleModal(true);
    setShowScheduleForm(false);
    setEditingSchedule(null);
  };

  const handleAddSchedule = () => {
    setEditingSchedule(null);
    setShowScheduleForm(true);
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setShowScheduleForm(true);
  };

  const handleSaveSchedule = async (scheduleData) => {
    try {
      setIsScheduleSubmitting(true);
      let response;

      if (editingSchedule) {
        response = await xService.updateSchedule(
          scheduleSearch.id,
          editingSchedule.schedule_id,
          scheduleData
        );
      } else {
        response = await xService.createSchedule(scheduleSearch.id, scheduleData);
      }

      // Update warnings if present
      if (response.warnings && response.warnings.length > 0) {
        setScheduleWarnings(prev => ({
          ...prev,
          [response.schedule.schedule_id]: response.warnings
        }));
      }

      // Refresh searches to get updated schedules
      await fetchSearches();
      
      // Fetch the updated search directly to ensure we have the latest data
      const updatedSearchData = await xService.getSearch(scheduleSearch.id);
      setScheduleSearch(updatedSearchData.search);

      setShowScheduleForm(false);
      setEditingSchedule(null);
    } catch (err) {
      const errorMessage = 'Failed to save schedule: ' + (err.response?.data?.detail || err.message);
      console.error('Error saving schedule:', err);
      console.error(errorMessage);
      alert(errorMessage);
    } finally {
      setIsScheduleSubmitting(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      setDeletingScheduleId(scheduleId);
      await xService.deleteSchedule(scheduleSearch.id, scheduleId);
      
      // Refresh searches
      await fetchSearches();
      
      // Fetch the updated search directly to ensure we have the latest data
      const updatedSearchData = await xService.getSearch(scheduleSearch.id);
      setScheduleSearch(updatedSearchData.search);

      // Remove warnings for deleted schedule
      setScheduleWarnings(prev => {
        const updated = { ...prev };
        delete updated[scheduleId];
        return updated;
      });
    } catch (err) {
      console.error('Error deleting schedule:', err);
      alert('Failed to delete schedule: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingScheduleId(null);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort searches
  const getFilteredAndSortedSearches = () => {
    let filtered = [...searches];

    // Apply search filter
    if (searchText.trim()) {
      const lowerSearch = searchText.toLowerCase();
      filtered = filtered.filter(search => 
        (search.name?.toLowerCase().includes(lowerSearch)) ||
        (search.description?.toLowerCase().includes(lowerSearch)) ||
        (search.query?.toLowerCase().includes(lowerSearch))
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'max_results':
          aVal = a.max_results || 0;
          bVal = b.max_results || 0;
          break;
        case 'status':
          aVal = a.is_active ? 1 : 0;
          bVal = b.is_active ? 1 : 0;
          break;
        case 'last_run_at':
          aVal = a.last_run_at ? new Date(a.last_run_at).getTime() : 0;
          bVal = b.last_run_at ? new Date(b.last_run_at).getTime() : 0;
          break;
        case 'created_at':
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  // ==================== Notification Functions ====================

  const fetchNotifications = async () => {
    try {
      setNotificationsLoading(true);
      const data = await xService.getAllNotifications();
      setNotifications(data.notifications || []);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      alert('Failed to load notifications: ' + (err.response?.data?.detail || err.message));
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleCreateNotification = () => {
    setIsEditingNotification(false);
    setSelectedNotification(null);
    setShowNotificationModal(true);
  };

  const handleEditNotification = (notification) => {
    setIsEditingNotification(true);
    setSelectedNotification(notification);
    setShowNotificationModal(true);
  };

  const handleDeleteNotification = async (notificationId) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      await xService.deleteNotification(notificationId);
      await fetchNotifications();
    } catch (err) {
      console.error('Error deleting notification:', err);
      alert('Failed to delete notification: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSaveNotification = async (formData) => {
    try {
      setIsNotificationSubmitting(true);
      if (isEditingNotification && selectedNotification) {
        await xService.updateNotification(selectedNotification.id, formData);
      } else {
        await xService.createNotification(formData);
      }
      setShowNotificationModal(false);
      await fetchNotifications();
    } catch (err) {
      console.error('Error saving notification:', err);
      alert('Failed to save notification: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsNotificationSubmitting(false);
    }
  };

  // ==================== Group Functions ====================

  const fetchGroups = async () => {
    try {
      setGroupsLoading(true);
      const data = await xService.getAllGroups();
      setGroups(data.groups || []);
    } catch (err) {
      console.error('Error fetching groups:', err);
      alert('Failed to load groups: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGroupsLoading(false);
    }
  };

  const handleCreateGroup = () => {
    setIsEditingGroup(false);
    setSelectedGroup(null);
    setShowGroupModal(true);
  };

  const handleEditGroup = (group) => {
    setIsEditingGroup(true);
    setSelectedGroup(group);
    setShowGroupModal(true);
  };

  const handleCloneGroup = (group) => {
    setIsEditingGroup(false);
    setSelectedGroup({
      name: `${group.name} (Copy)`,
      x_user_ids: [...group.x_user_ids]
    });
    setShowGroupModal(true);
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) {
      return;
    }

    try {
      await xService.deleteGroup(groupId);
      await fetchGroups();
    } catch (err) {
      console.error('Error deleting group:', err);
      alert('Failed to delete group: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSaveGroup = async (formData) => {
    try {
      if (isEditingGroup && selectedGroup) {
        await xService.updateGroup(selectedGroup.id, formData);
      } else {
        await xService.createGroup(formData);
      }
      setShowGroupModal(false);
      await fetchGroups();
    } catch (err) {
      console.error('Error saving group:', err);
      throw err;
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4 align-items-center">
        <div className="col">
          <h2>
            <i className="bi bi-twitter-x me-2"></i>
            Search Management
          </h2>
          <p className="text-muted mb-0">
            Manage searches, schedules, and notifications for X posts
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <ul className="nav nav-tabs mb-4" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'searches' ? 'active' : ''}`}
            onClick={() => setActiveTab('searches')}
            type="button"
            role="tab"
          >
            <i className="bi bi-search me-2"></i>
            Search Config
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
            type="button"
            role="tab"
          >
            <i className="bi bi-bell me-2"></i>
            Notifications Config
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'groups' ? 'active' : ''}`}
            onClick={() => setActiveTab('groups')}
            type="button"
            role="tab"
          >
            <i className="bi bi-people me-2"></i>
            Group Configs
          </button>
        </li>
      </ul>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Search Config Tab */}
        {activeTab === 'searches' && (
          <div className="tab-pane fade show active">
            <div className="row mb-3 align-items-center">
              <div className="col">
                <p className="text-muted mb-0">
                  Create, edit, and run searches on recent X posts
                </p>
              </div>
              <div className="col-auto">
                <button
                  className="btn btn-primary"
                  onClick={handleCreate}
                  disabled={loading}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Create New Search
                </button>
              </div>
            </div>

            {/* Alert for API configuration */}
            {!apiConfigured && (
              <div className="alert alert-warning d-flex align-items-center" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <div>
                  X API is not configured. Contact your administrator to set up API credentials.
                </div>
              </div>
            )}

            {/* Error display */}
            {error && !apiConfigured && (
              <div className="alert alert-danger" role="alert">
                <i className="bi bi-exclamation-circle me-2"></i>
                {error}
              </div>
            )}

            {/* Search filter */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name, description, or query..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  {searchText && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchText('')}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Searches list */}
            <div className="row">
              <div className="col">
                <XSearchList
                  searches={getFilteredAndSortedSearches()}
                  loading={loading}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onRun={handleRun}
                  onViewResults={handleViewResults}
                  onManageSchedules={handleManageSchedules}
                  runningSearchId={runningSearchId}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
              </div>
            </div>
          </div>
        )}

        {/* Notifications Config Tab */}
        {activeTab === 'notifications' && (
          <div className="tab-pane fade show active">
            <div className="row mb-3 align-items-center">
              <div className="col">
                <p className="text-muted mb-0">
                  Configure scheduled notifications that analyze search results and send summaries to MS Teams
                </p>
              </div>
              <div className="col-auto">
                <button
                  className="btn btn-primary"
                  onClick={handleCreateNotification}
                  disabled={notificationsLoading}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Create New Notification
                </button>
              </div>
            </div>

            <div className="row">
              <div className="col">
                <XNotificationList
                  notifications={notifications}
                  loading={notificationsLoading}
                  onEdit={handleEditNotification}
                  onDelete={handleDeleteNotification}
                />
              </div>
            </div>
          </div>
        )}

        {/* Group Configs Tab */}
        {activeTab === 'groups' && (
          <div className="tab-pane fade show active">
            <div className="row mb-3 align-items-center">
              <div className="col">
                <p className="text-muted mb-0">
                  Organize X users into groups for easier management
                </p>
              </div>
              <div className="col-auto">
                <button
                  className="btn btn-primary"
                  onClick={handleCreateGroup}
                  disabled={groupsLoading}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Create New Group
                </button>
              </div>
            </div>

            <div className="row">
              <div className="col">
                <XGroupList
                  groups={groups}
                  loading={groupsLoading}
                  onEdit={handleEditGroup}
                  onDelete={handleDeleteGroup}
                  onClone={handleCloneGroup}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Search Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isEditing ? 'Edit Search' : 'Create New Search'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <XSearchForm
                  initialData={selectedSearch}
                  isEditing={isEditing}
                  onSave={handleSave}
                  onCancel={() => setShowModal(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {showNotificationModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isEditingNotification ? 'Edit Notification' : 'Create New Notification'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowNotificationModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <XNotificationForm
                  notification={selectedNotification}
                  onSubmit={handleSaveNotification}
                  onCancel={() => setShowNotificationModal(false)}
                  isSubmitting={isNotificationSubmitting}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Modal */}
      {showResults && selectedSearchId && (
        <XSearchResultsWithHistory
          searchId={selectedSearchId}
          onClose={() => {
            setShowResults(false);
            setSelectedSearchId(null);
          }}
        />
      )}

      {/* Group Modal */}
      {showGroupModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isEditingGroup ? 'Edit Group' : 'Create New Group'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowGroupModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <XGroupForm
                  initialData={selectedGroup}
                  isEditing={isEditingGroup}
                  onSave={handleSaveGroup}
                  onCancel={() => setShowGroupModal(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Management Modal */}
      {showScheduleModal && scheduleSearch && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-calendar-event me-2"></i>
                  Manage Schedules - {scheduleSearch.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowScheduleModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {!showScheduleForm ? (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <p className="text-muted mb-0">
                        Configure automatic schedules for this search
                      </p>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleAddSchedule}
                      >
                        <i className="bi bi-plus-circle me-1"></i>
                        Add Schedule
                      </button>
                    </div>
                    
                    <XSearchScheduleList
                      schedules={scheduleSearch.schedules || []}
                      warnings={scheduleWarnings}
                      onEdit={handleEditSchedule}
                      onDelete={handleDeleteSchedule}
                      deletingScheduleId={deletingScheduleId}
                    />
                  </>
                ) : (
                  <div>
                    <h6 className="mb-3">
                      {editingSchedule ? 'Edit Schedule' : 'Create New Schedule'}
                    </h6>
                    <XSearchScheduleForm
                      schedule={editingSchedule}
                      onSubmit={handleSaveSchedule}
                      onCancel={() => {
                        setShowScheduleForm(false);
                        setEditingSchedule(null);
                      }}
                      isSubmitting={isScheduleSubmitting}
                    />
                  </div>
                )}
              </div>
              {!showScheduleForm && (
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowScheduleModal(false)}
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default XSearchPage;
